import { createI18n, getActiveI18n } from "@magic-md/i18n";
import type { LocaleParams } from "@magic-md/i18n";
import type { PluginI18n, PluginManifest } from "./types/index";

const LOCAL_STATIC_MESSAGE_REF_RE = /^%([^%\s]+)%$/;

export interface ResolvePluginTextOptions {
    locale?: string | null;
    fallbackLocale?: string;
    params?: LocaleParams;
}

export function resolvePluginText(
    value: string | undefined,
    i18n: PluginI18n | undefined,
    options: ResolvePluginTextOptions = {},
): string | undefined {
    if (value === undefined || !i18n) return value;

    const match = LOCAL_STATIC_MESSAGE_REF_RE.exec(value.trim());
    if (!match) return value;

    const active = getActiveI18n();
    const scoped = createI18n({
        requestedLocale: options.locale ?? active.locale,
        fallbackLocale: options.fallbackLocale ?? active.fallbackLocale,
        bundles: i18n.bundles,
    });
    const translated = scoped.t(i18n.namespace, match[1], options.params);
    return translated === match[1] ? value : translated;
}

export function resolvePluginValue<T>(
    value: T,
    i18n: PluginI18n | undefined,
    options: ResolvePluginTextOptions = {},
): T {
    if (!i18n) return value;
    if (typeof value === "string") {
        return resolvePluginText(value, i18n, options) as T;
    }
    if (Array.isArray(value)) {
        return value.map((item) => resolvePluginValue(item, i18n, options)) as T;
    }
    if (value && typeof value === "object") {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, item]) => [
                key,
                resolvePluginValue(item, i18n, options),
            ]),
        ) as T;
    }
    return value;
}

export function resolvePluginManifest(
    manifest: PluginManifest,
    i18n: PluginI18n | undefined,
    options: ResolvePluginTextOptions = {},
): PluginManifest {
    return resolvePluginValue(manifest, i18n, options);
}
